import type { AccountState } from './account-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';
import {
  Action,
  updateStatusById,
  updateStatus,
  StateStatus,
  updateResourceByParam,
  setStateDirtyByParam,
  updateResourceByObjectKey,
} from '../reducers/utils/state-manager';

import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: AccountState = {
  accountsById: {},
  accountsStateById: {},
  accountsStateByUser: {},
  billingInfoById: {},
  billingInfoStateByAccountId: StateStatus.notAttempted,
  userActivitySubscriptionsByAccountId: {},
  userActivitySubscriptionsByAccountIdStatus: {},
  stripeBasePlans: null,
  stripeBasePlansStatus: StateStatus.notAttempted,
  // In V3, we are using Prices instead of Plans to track and calculate subscription costs.
  // V3 prices also support both standard pricing (with amount field) and tiered pricing (in tiers)
  stripeBasePricesV3: null,
  stripeBasePricesV3Status: StateStatus.notAttempted,
  accountActivityNotificationsByAccountId: {},
  accountActivityNotificationsByAccountIdStatus: {},
  messageServiceStatusById: {},
  messageServiceStatusStateByAccountId: StateStatus.notAttempted,
  cancellationDetailsByAccountId: {},
  cancellationDetailsStateByAccountId: StateStatus.notAttempted,
  messageCountsByAccountId: {},
  messageCountsStateByAccountId: StateStatus.notAttempted,
};

const updateBillingInfo = (
  state: AccountState,
  accountId: number,
  update: Partial<AccountBillingInfo>,
) => {
  const billingInfoById = { ...state.billingInfoById };
  billingInfoById[accountId] = { ...billingInfoById[accountId], ...update };

  return { ...state, billingInfoById };
};

export const accountReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_ACCOUNT'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'accounts', 'id', accountId, true);
    }
    case SUCCESS('LOAD_ACCOUNT'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'accounts', 'id', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('LOAD_ACCOUNT'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'accounts', 'id', accountId, false);
    }
    // Accounts are the top level entity for us, so you can load them just by using /accounts with no user configured
    // Since it's hard to get the username here, and it doesn't affect anything, we just use the key 'USER' to track the
    // status of the top level request
    case STARTING('LOAD_ACCOUNTS'): {
      return updateResourceByParam(state, 'accounts', 'user', 'user', true);
    }
    case SUCCESS('LOAD_ACCOUNTS'): {
      const { data } = action.payload;
      return updateResourceByParam(
        state,
        'accounts',
        'user',
        'user',
        false,
        convertArrayToMap(data, (a: TeamEngineAccount) => String(a.accountId)),
      );
    }
    case FAILURE('LOAD_ACCOUNTS'): {
      return updateResourceByParam(state, 'accounts', 'user', 'user', false);
    }
    case SUCCESS('RETRY_BUNDLE_REGISTRATION'):
    case SUCCESS('CREATE_TWILIO_PROFILE_AND_BRAND'): {
      const { accountId } = action.payload.args;
      const newState = setStateDirtyByParam(state, 'accounts', 'user', 'user');
      return setStateDirtyByParam(newState, 'accounts', 'id', accountId);
    }
    case STARTING('GET_STRIPE_BASE_PLANS'): {
      return updateStatus(state, 'stripeBasePlans', true);
    }
    case SUCCESS('GET_STRIPE_BASE_PLANS'): {
      const { data } = action.payload;
      return updateStatus(state, 'stripeBasePlans', false, data);
    }
    case FAILURE('GET_STRIPE_BASE_PLANS'): {
      return updateStatus(state, 'stripeBasePlans', false);
    }
    case STARTING('GET_STRIPE_BASE_PRICES_V3'): {
      return updateStatus(state, 'stripeBasePricesV3', true);
    }
    case SUCCESS('GET_STRIPE_BASE_PRICES_V3'): {
      const { data } = action.payload;
      return updateStatus(state, 'stripeBasePricesV3', false, data);
    }
    case FAILURE('GET_STRIPE_BASE_PRICES_V3'): {
      return updateStatus(state, 'stripeBasePricesV3', false);
    }
    case STARTING('SET_ACCOUNT_STATUS'):
    case STARTING('GET_BILLING_INFO'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'billingInfo', 'accountId', accountId, true);
    }
    case SUCCESS('GET_BILLING_INFO'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'billingInfo', 'accountId', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('GET_BILLING_INFO'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'billingInfo', 'accountId', accountId, false);
    }
    case SUCCESS('UPDATE_SUBSCRIPTION'):
    case SUCCESS('UPDATE_BASE_PLAN'): {
      const {
        data: account,
        args: { accountId },
      } = action.payload;
      let newState = setStateDirtyByParam(state, 'billingInfo', 'accountId', accountId);

      if (account) {
        newState = {
          ...newState,
          accountsById: {
            ...newState.accountsById,
            [account.accountId]: account,
          },
        };
      } else {
        newState = setStateDirtyByParam(newState, 'accounts', 'user', 'user');
        newState = setStateDirtyByParam(newState, 'accounts', 'id', accountId);
      }
      return newState;
    }
    case SUCCESS('SET_ACCOUNT_STATUS'): {
      const { accountId } = action.payload.args;
      let newState = setStateDirtyByParam(state, 'accounts', 'user', 'user');
      newState = setStateDirtyByParam(newState, 'accounts', 'id', accountId);
      return setStateDirtyByParam(newState, 'billingInfo', 'accountId', accountId);
    }
    case SUCCESS('UPDATE_BILLING_EMAIL'): {
      const { accountId, newEmailAddress } = action.payload.args;
      const billingInfoById = { ...state.billingInfoById };
      billingInfoById[accountId] = {
        ...billingInfoById[accountId],
        stripeBilling: {
          ...billingInfoById[accountId].stripeBilling,
          email: newEmailAddress,
        },
      };

      return { ...state, billingInfoById };
    }
    case 'SAVE_AD_SPEND_STARTING': {
      return updateBillingInfo(state, action.payload.accountId, {
        savingAdSpend: true,
      });
    }
    case 'SAVE_AD_SPEND_SUCCESS': {
      return updateBillingInfo(state, action.payload.accountId, {
        savingAdSpend: false,
        billingInfoLoaded: false,
      });
    }
    case 'SAVE_AD_SPEND_FAILURE': {
      return updateBillingInfo(state, action.payload.accountId, {
        savingAdSpend: false,
      });
    }
    case STARTING('LOAD_ACTIVE_USER_SUBSCRIPTIONS'): {
      const { accountId } = action.payload.args;
      return updateStatusById(state, 'userActivitySubscriptionsByAccountId', true, accountId);
    }
    case SUCCESS('LOAD_ACTIVE_USER_SUBSCRIPTIONS'): {
      const { args, data } = action.payload;
      return updateStatusById(
        state,
        'userActivitySubscriptionsByAccountId',
        false,
        args.accountId,
        data,
      );
    }
    case SUCCESS('ADD_ACTIVE_USER_SUBSCRIPTION'): {
      const { accountId } = action.payload.args;
      const accountSubscriptions = state.userActivitySubscriptionsByAccountId[accountId];
      if (!accountSubscriptions) {
        const newSubscriptions = {
          ...state.userActivitySubscriptionsByAccountId,
          [accountId]: [action.payload.data],
        };
        return { ...state, userActivitySubscriptionsByAccountId: newSubscriptions };
      }

      const newAccountSubscriptions = accountSubscriptions.slice();
      newAccountSubscriptions.push(action.payload.data);
      const newSubscriptions = {
        ...state.userActivitySubscriptionsByAccountId,
        [action.payload.args.accountId]: newAccountSubscriptions,
      };
      return { ...state, userActivitySubscriptionsByAccountId: newSubscriptions };
    }
    case SUCCESS('UNSUBSCRIBE_TO_USER_ACTIVITY'): {
      const { username, accountId } = action.payload.args;
      const accountSubscriptions = state.userActivitySubscriptionsByAccountId[accountId];
      const subIndex = accountSubscriptions.findIndex((s) => s.subscribeToUser === username);
      if (subIndex === -1) {
        return state;
      }
      const newAccountSubscriptions = accountSubscriptions.slice();
      newAccountSubscriptions.splice(subIndex, 1);
      const newSubscriptions = {
        ...state.userActivitySubscriptionsByAccountId,
        [action.payload.args.accountId]: newAccountSubscriptions,
      };
      return { ...state, userActivitySubscriptionsByAccountId: newSubscriptions };
    }
    case STARTING('UPDATE_ACCOUNT'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'accounts', 'accountId', accountId, true);
    }
    case SUCCESS('RENEW_ANNUAL_ACCOUNT'):
    case SUCCESS('UPDATE_ACCOUNT'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      if (!data) {
        // The renew annual account might return null if it fails for a known reason.
        // If so, then nothing to update
        return state;
      }
      return updateResourceByParam(state, 'accounts', 'accountId', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('UPDATE_ACCOUNT'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'accounts', 'accountId', accountId, false);
    }
    case SUCCESS('CANCEL_SUBSCRIPTION'): {
      const {
        args: { accountId },
        data: { account },
      } = action.payload;
      const newBillingInfoState = setStateDirtyByParam(
        state,
        'billingInfo',
        'accountId',
        accountId,
      );
      const newState = updateResourceByParam(state, 'accounts', 'accountId', accountId, false, {
        [accountId]: account,
      });
      return {
        ...newState,
        billingInfoStateByAccountId: newBillingInfoState.billingInfoStateByAccountId,
      };
    }
    case SUCCESS('HANDLE_ANNUAL_SUBSCRIPTION_RENEWAL_RESPONSE'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'accounts', 'accountId', accountId, false, {
        [accountId]: data,
      });
    }
    case SUCCESS('UPDATE_TRIAL_END_DATE'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'accounts', 'accountId', accountId, false, {
        [accountId]: data.account,
      });
    }
    case STARTING('LOAD_ACCOUNT_ACTIVITY_NOTIFICATIONS'): {
      const { accountId } = action.payload.args;
      return updateStatusById(state, 'accountActivityNotificationsByAccountId', true, accountId);
    }
    case SUCCESS('LOAD_ACCOUNT_ACTIVITY_NOTIFICATIONS'): {
      const { args, data } = action.payload;
      return updateStatusById(
        state,
        'accountActivityNotificationsByAccountId',
        false,
        args.accountId,
        data.notifications,
      );
    }
    case FAILURE('LOAD_ACCOUNT_ACTIVITY_NOTIFICATIONS'): {
      const { accountId } = action.payload.args;
      return updateStatusById(state, 'accountActivityNotificationsByAccountId', false, accountId);
    }
    case SUCCESS('SUBSCRIBE_TO_ACCOUNT_ACTIVITY_NOTIFICATION'): {
      const { args, data } = action.payload;
      const accountActivityNotifications =
        state.accountActivityNotificationsByAccountId[args.accountId];
      if (!accountActivityNotifications) {
        const newSubscriptions = {
          ...state.accountActivityNotificationsByAccountId,
          [args.accountId]: [data.notification],
        };
        return { ...state, accountActivityNotificationsByAccountId: newSubscriptions };
      }

      // because there is only one type of notification right now, we can simply remove and replace the existing notification for the given user
      const newAccountSubscriptions = accountActivityNotifications.filter(
        (n) => n.username !== data.username,
      );
      newAccountSubscriptions.push(data.notification);
      const newSubscriptions = {
        ...state.accountActivityNotificationsByAccountId,
        [args.accountId]: newAccountSubscriptions,
      };
      return { ...state, accountActivityNotificationsByAccountId: newSubscriptions };
    }
    case SUCCESS('UNSUBSCRIBE_TO_ACCOUNT_ACTIVITY_NOTIFICATION'): {
      const { accountId, accountActivityNotificationId } = action.payload.args;
      const accountActivityNotifications = state.accountActivityNotificationsByAccountId[accountId];
      const updatedAccountActivityNotifications = accountActivityNotifications.filter(
        (n) => n.accountActivityNotificationId !== accountActivityNotificationId,
      );

      const updatedNotifications = {
        ...state.accountActivityNotificationsByAccountId,
        [accountId]: updatedAccountActivityNotifications,
      };
      return { ...state, accountActivityNotificationsByAccountId: updatedNotifications };
    }
    case SUCCESS('UPDATE_ACCOUNT_ACTIVITY_NOTIFICATION'): {
      const { accountId, accountActivityNotificationId } = action.payload.args;
      const { notification } = action.payload.data;
      const accountActivityNotifications = state.accountActivityNotificationsByAccountId[accountId];
      const updatedAccountActivityNotifications = accountActivityNotifications.map((n) => {
        if (n.accountActivityNotificationId === accountActivityNotificationId) {
          return notification;
        }

        return n;
      });

      const updatedNotifications = {
        ...state.accountActivityNotificationsByAccountId,
        [accountId]: updatedAccountActivityNotifications,
      };
      return { ...state, accountActivityNotificationsByAccountId: updatedNotifications };
    }
    case STARTING('MESSAGE_SERVICE_STATUS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'messageServiceStatus', 'accountId', accountId, true);
    }
    case SUCCESS('MESSAGE_SERVICE_STATUS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'messageServiceStatus', 'accountId', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('MESSAGE_SERVICE_STATUS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'messageServiceStatus', 'accountId', accountId, false);
    }
    case STARTING('LOAD_CANCEL_DETAILS'): {
      const { accountId } = action.payload.args;
      return updateResourceByObjectKey(state, 'cancellationDetailsByAccountId', accountId, true);
    }
    case SUCCESS('LOAD_CANCEL_DETAILS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByObjectKey(state, 'cancellationDetailsByAccountId', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('LOAD_CANCEL_DETAILS'): {
      const { accountId } = action.payload.args;
      return updateResourceByObjectKey(state, 'cancellationDetailsByAccountId', accountId, false);
    }
    case STARTING('LOAD_MESSAGE_COUNTS'): {
      const { accountId } = action.payload.args;
      return updateResourceByObjectKey(state, 'messageCountsByAccountId', accountId, true);
    }
    case SUCCESS('LOAD_MESSAGE_COUNTS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByObjectKey(state, 'messageCountsByAccountId', accountId, false, {
        [accountId]: data,
      });
    }
    case FAILURE('LOAD_MESSAGE_COUNTS'): {
      const { accountId } = action.payload.args;
      return updateResourceByObjectKey(state, 'messageCountsByAccountId', accountId, false);
    }
    default:
      return state;
  }
};
